import { useState } from 'react';
import { Button, Modal, Table, Tag, Typography } from 'antd';

const { Title } = Typography;

const formatHours = (hours) => {
    if (hours == null) return '-';
    const h = Math.floor(hours);
    const m = Math.floor((hours % 1) * 60);
    const s = Math.floor(((hours % 1) * 60 % 1) * 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const formatDateTime = (value) => {
    if (!value) {
        return '-';
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
        return value;
    }

    return parsedDate.toLocaleString();
};

const getCsvFileName = (url) => {
    try {
        const parsedUrl = new URL(url);
        const pathParts = parsedUrl.pathname.split('/');
        const rawName = pathParts[pathParts.length - 1] || 'excavation-log.csv';
        return rawName.endsWith('.csv') ? rawName : `${rawName}.csv`;
    } catch (error) {
        return 'excavation-log.csv';
    }
};

const getLogFileDisplayName = (url) => {
    try {
        const parsedUrl = new URL(url);
        const pathParts = parsedUrl.pathname.split('/');
        const fileName = pathParts[pathParts.length - 1] || '-';
        return decodeURIComponent(fileName);
    } catch (error) {
        return '-';
    }
};

const handleDownloadCsv = async (url) => {
    try {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Download failed with status ${response.status}`);
        }

        const fileBlob = await response.blob();
        const csvBlob = new Blob([fileBlob], { type: 'text/csv;charset=utf-8;' });
        const downloadUrl = window.URL.createObjectURL(csvBlob);
        const link = document.createElement('a');

        link.href = downloadUrl;
        link.download = getCsvFileName(url);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
        window.open(url, '_blank', 'noopener,noreferrer');
    }
};

const timelineColumns = [
    {
        title: 'Start',
        dataIndex: 'start',
        key: 'start',
        render: (value) => formatDateTime(value),
    },
    {
        title: 'End',
        dataIndex: 'end',
        key: 'end',
        render: (value) => formatDateTime(value),
    },
    {
        title: 'Hours Used',
        key: 'hours_used',
        render: (_, row) => {
            const start = new Date(row.start);
            const end = new Date(row.end);
            const hours = (end - start) / (1000 * 60 * 60);
            return hours >= 0 ? formatHours(hours) : '-';
        },
    },
];

const usagePeriodColumns = (billingPreference) => [
    {
        title: 'Machine ID',
        dataIndex: 'machine_number',
        key: 'machine_number',
    },
    {
        title: 'Machine Name',
        dataIndex: 'machine_name',
        key: 'machine_name',
    },
    ...(billingPreference !== 'full_period' ? [
        {
            title: 'Total Hours',
            dataIndex: 'total_hours',
            key: 'total_hours',
            render: (value) => formatHours(value),
        },
        {
            title: 'Total Days',
            dataIndex: 'total_days',
            key: 'total_days',
        },
    ] : []),
    {
        title: 'Last Used',
        dataIndex: 'last_timestamp',
        key: 'last_timestamp',
        render: (value) => formatDateTime(value),
    },
    ...(billingPreference === 'full_period' ? [
        {
            title: 'Schedule Start',
            dataIndex: 'schedule_from',
            key: 'schedule_from',
            render: (value) => value ? new Date(value).toLocaleDateString() : '-',
        },
        {
            title: 'Schedule End',
            dataIndex: 'schedule_to',
            key: 'schedule_to',
            render: (value) => value ? new Date(value).toLocaleDateString() : '-',
        },
    ] : []),
];

const UsagePeriodModal = ({ visible, record, onClose }) => (
    <Modal title={'Usage Periods'} open={visible} onCancel={onClose} footer={null} width={800}>
        <Table
            columns={usagePeriodColumns(record?.billing_preference)}
            dataSource={record?.usage_periods ?? []}
            rowKey='id'
            pagination={false}
            expandable={{
                expandedRowRender: (period) => (
                    <Table
                        columns={timelineColumns}
                        dataSource={period.usages_timelines ?? []}
                        rowKey={(row) => `${row.start}-${row.end}`}
                        pagination={false}
                        size='small'
                    />
                ),
                rowExpandable: (period) => period.usages_timelines?.length > 0,
            }}
        />
    </Modal>
);

// ── GuidanceServiceLogs ───────────────────────────────────────────────────────
// Data shape: { id, log_file, created, updated, ... }

const columns = [
    {
        title: 'Log Type',
        dataIndex: 'log_type',
        key: 'log_type',
    },
    {
        title: 'Filename',
        dataIndex: 'log_file',
        key: 'log_filename',
        render: (value) => getLogFileDisplayName(value),
    },
    {
        title: 'Created',
        dataIndex: 'created',
        key: 'created',
        render: (value) => formatDateTime(value),
    },
    {
        title: 'Updated',
        dataIndex: 'updated',
        key: 'updated',
        render: (value) => formatDateTime(value),
    },
    {
        title: 'Download Log',
        dataIndex: 'log_file',
        key: 'log_file',
        render: (value) => (
            <Button
                type='link'
                className={'bordered-default-button'}
                href={value}
                onClick={(event) => {
                    event.preventDefault();
                    handleDownloadCsv(value);
                }}
            >
                Download
            </Button>
        ),
    },
];

export const GuidanceServiceLogs = ({ data = [] }) => {
    return (
        <div>
            <Title level={3}>Guidance Service Logs</Title>
            <Table columns={columns} dataSource={data} pagination={false} rowKey='id' />
        </div>
    );
};

// ── GuidanceServiceLogs ─────────────────────────────────────────────────────
const serviceLogColumns = (onViewClick) => [
    {
        title: 'Service Name',
        dataIndex: 'service_type',
        key: 'service_type',
        render: (value) => ({ excavation: 'Excavation Record', boring: 'Boring Guidance' }[value] ?? value),
    },
    {
        title: 'Billing Preference',
        dataIndex: 'billing_preference',
        key: 'billing_preference',
        render: (value) => ({
            hourly: 'Hourly',
            daily: 'Daily',
            full_period: 'Machine Schedule Period',
        }[value] ?? value),
    },
    {
        title: 'Status',
        dataIndex: 'is_active',
        key: 'is_active',
        render: (value) => (
            <Tag color={value ? 'green' : 'default'}>{value ? 'Active' : 'Inactive'}</Tag>
        ),
    },
    {
        title: 'Last Used',
        key: 'last_used',
        render: (_, record) => {
            const timestamps = (record.usage_periods ?? [])
                .map((p) => p.last_timestamp)
                .filter(Boolean);
            if (!timestamps.length) return '-';
            const latest = timestamps.reduce((a, b) => (a > b ? a : b));
            return formatDateTime(latest);
        },
    },
    {
        title: 'Usage Periods',
        key: 'usage_periods',
        render: (_, record) => (
            <Button
                type='default'
                onClick={() => onViewClick(record)}
                disabled={!record.usage_periods?.length}
            >
                View
            </Button>
        ),
    },
];

export const ServiceUsages = ({ data = [] }) => {
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);

    return (
        <div>
            <Title level={3}>Guidance Service Usages</Title>
            <Table
                columns={serviceLogColumns((record) => {
                    setSelectedRecord(record);
                    setModalVisible(true);
                })}
                dataSource={data}
                pagination={false}
                rowKey='id'
            />
            <UsagePeriodModal
                visible={modalVisible}
                record={selectedRecord}
                onClose={() => {
                    setModalVisible(false);
                    setSelectedRecord(null);
                }}
            />
        </div>
    );
};
